import { Link } from "wouter";

interface StoreCardProps {
  store: any;
}

// 既存の画像をローテーションで使用
const STORE_IMAGES = [
  "/store-entrance.jpg",
  "/store-seating.jpg",
  "/store-aisle.jpg",
  "/store-restroom.jpg",
];

function getStoreImage(storeId: number): string {
  return STORE_IMAGES[storeId % STORE_IMAGES.length];
}

export default function StoreCard({ store }: StoreCardProps) {
  const thumbnailUrl = store.thumbnailUrl || getStoreImage(store.id);

  // Parse features from JSON if string
  const features = typeof store.features === 'string'
    ? JSON.parse(store.features || '[]')
    : (store.features || []);

  return (
    <Link href={`/stores/${store.id}`}>
      <div className="card-hover group cursor-pointer h-full">
        {/* Image */}
        <div className="aspect-[4/3] overflow-hidden bg-stone-200 relative">
          <img
            src={thumbnailUrl}
            alt={store.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />

          {/* Top badges */}
          <div className="absolute top-3 left-3 flex gap-2">
            {store.isRainyDayOk && (
              <span className="bg-white/90 backdrop-blur-sm text-stone-900 rounded-full px-2 py-1 text-xs font-medium">
                雨の日OK
              </span>
            )}
          </div>

          {/* Entry difficulty score */}
          {store.entryDifficulty && (
            <div className="absolute top-3 right-3">
              <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1">
                <svg className="h-3.5 w-3.5 text-amber-500 fill-amber-500" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <span className="text-xs font-semibold text-stone-900">
                  {store.entryDifficulty}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="mb-3">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-lg text-stone-900 line-clamp-1 group-hover:text-teal-600 transition-colors">
                {store.name}
              </h3>
              <span className="tag-outline shrink-0">
                {store.area}
              </span>
            </div>
            {store.nearestStation && (
              <p className="text-sm text-stone-500 flex items-center gap-1">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {store.nearestStation}
                {store.walkingMinutes && ` 徒歩${store.walkingMinutes}分`}
              </p>
            )}
          </div>

          {store.description && (
            <p className="text-sm text-stone-500 mb-4 line-clamp-2">
              {store.description}
            </p>
          )}

          {/* Feature tags */}
          <div className="flex flex-wrap gap-1.5">
            {store.strollerEntry && (
              <span className="tag-teal">そのまま着席</span>
            )}
            {store.hasElevator && (
              <span className="tag-amber">エレベーター</span>
            )}
            {store.hasDiaperChanging && (
              <span className="tag-rose">オムツ替え</span>
            )}
            {features.slice(0, 2).map((feature: string) => (
              <span key={feature} className="tag-sky">
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}
